/**
 * RatingPrimer — our OWN "are you feeling the game?" pre-prompt, shown BEFORE the
 * native store review sheet.
 *
 * This is the sentiment funnel. The native review sheet is silent and OS-quota-
 * limited (we can't ask often), so we only want to spend an ask on a happy
 * player. show() resolves:
 *   'love'  → caller fires ratingService.requestReview() (native sheet)
 *   'meh'   → caller routes the player to the in-app feedback form (NOT the store)
 *   'later' → caller does nothing this time; the cooldown re-asks later
 *
 * Compliance: this UI shows NO star rating and does not mimic the App Store. It
 * only asks sentiment. The actual rating is collected by the OS sheet, never
 * here. That is what keeps the funnel App-Store-safe.
 *
 * Styled to match NotificationPrimer (neon-noir panel), in gold/green because it
 * rides a winning moment.
 */

export type RatingChoice = 'love' | 'meh' | 'later';

const STYLE_ID = 'sdl-rate-primer-styles';

export class RatingPrimer {
  public element: HTMLElement;
  private visible = false;
  private resolver: ((choice: RatingChoice) => void) | null = null;

  constructor() {
    RatingPrimer.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-rprime-host';
    this.element.innerHTML = `
      <div class="sdl-rprime-backdrop"></div>
      <div class="sdl-rprime-panel">
        <div class="sdl-rprime-icon">🎲</div>
        <div class="sdl-rprime-title">FEELING THE GAME?</div>
        <div class="sdl-rprime-msg">You're stacking wins on the block. Show Street Dice Legends some love and help the crew grow.</div>
        <button class="sdl-rprime-btn sdl-rprime-yes" data-love>LOVING IT</button>
        <button class="sdl-rprime-btn sdl-rprime-no" data-meh>NOT REALLY</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.element.querySelector('[data-love]')!.addEventListener('click', () => this.resolve('love'));
    this.element.querySelector('[data-meh]')!.addEventListener('click', () => this.resolve('meh'));
    // Backdrop tap = "ask me later" (recoverable; the cooldown handles re-asking).
    this.element.querySelector('.sdl-rprime-backdrop')!.addEventListener('click', () => this.resolve('later'));
  }

  /** Show the primer. Resolves with the player's sentiment choice. */
  show(): Promise<RatingChoice> {
    if (this.visible) return Promise.resolve('later');
    this.visible = true;
    this.element.classList.add('visible');
    return new Promise<RatingChoice>((res) => { this.resolver = res; });
  }

  private resolve(choice: RatingChoice): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    const r = this.resolver;
    this.resolver = null;
    r?.(choice);
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-rprime-host {
        position: fixed; inset: 0;
        z-index: 905;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-rprime-host.visible { display: flex; }
      .sdl-rprime-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75);
        backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);
      }
      .sdl-rprime-panel {
        position: relative;
        width: 300px;
        padding: 26px 22px 16px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(255,215,0,0.45);
        border-radius: 20px;
        display: flex; flex-direction: column; align-items: center;
        gap: 8px;
        box-shadow: 0 18px 50px rgba(0,0,0,0.6);
        animation: sdl-rprime-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes sdl-rprime-in {
        0% { opacity: 0; transform: scale(0.8); }
        100% { opacity: 1; transform: scale(1); }
      }
      .sdl-rprime-icon {
        font-size: 40px;
        line-height: 1;
        margin-bottom: 2px;
      }
      .sdl-rprime-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 26px; letter-spacing: 1px; text-align: center;
        color: #fff;
      }
      .sdl-rprime-msg {
        font-family: 'Rajdhani', sans-serif; font-weight: 500;
        font-size: 14px; line-height: 1.45; text-align: center;
        color: rgba(255,255,255,0.72);
        margin-bottom: 10px;
      }
      .sdl-rprime-btn {
        width: 100%;
        padding: 13px 0;
        border: none; border-radius: 13px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px; letter-spacing: 2px;
        cursor: pointer;
        transition: transform 0.1s, background 0.15s, filter 0.15s;
      }
      .sdl-rprime-yes {
        background: linear-gradient(135deg, #FFD700, #B8960F);
        color: #1a1400;
        margin-bottom: 4px;
      }
      .sdl-rprime-yes:active { transform: scale(0.97); filter: brightness(1.1); }
      .sdl-rprime-no {
        background: rgba(255,255,255,0.08);
        color: rgba(255,255,255,0.66);
      }
      .sdl-rprime-no:active { transform: scale(0.97); background: rgba(255,255,255,0.14); }
    `;
    document.head.appendChild(style);
  }
}
